import { format2 } from "./utils/format2";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  writeBatch,
  increment
} from "firebase/firestore";
// import { useTheme } from "./ThemeContext"; // Removed unused import
import styles from "./ProductionAnalyticsDashboard.module.css";

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isEpochDate = (value) => {
  const d = toJsDate(value);
  return !!d && d.getTime() === 0;
};

export default function ProductionAnalyticsDashboard() {
  const navigate = useNavigate();
  const [productionEvents, setProductionEvents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterProduct, setFilterProduct] = useState("");
  const [selectedGraphProduct, setSelectedGraphProduct] = useState("");
  // Removed empty destructuring from useTheme
  const db = getFirestore();

  const fetchProductionEvents = useCallback(async () => {
    setLoading(true);
    try {
      const [eventsSnapshot, productsSnapshot] = await Promise.all([
        getDocs(collection(db, "productionEvents")),
        getDocs(collection(db, "products"))
      ]);
      
      const events = eventsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProductionEvents(events);
      
      const productsData = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(productsData);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchProductionEvents();
  }, [fetchProductionEvents]);

  // Delete production event
  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this production record?")) return;

    try {
      const now = new Date();

      const eventRef = doc(db, "productionEvents", eventId);
      const eventSnap = await getDoc(eventRef);

      if (!eventSnap.exists()) {
        // Already deleted or never existed.
        setProductionEvents(productionEvents.filter(event => event.id !== eventId));
        return;
      }

      const event = { id: eventSnap.id, ...eventSnap.data() };
      const eventCreatedAt = toJsDate(event?.createdAt) || toJsDate(event?.date);
      const eventCreatedAtMs = eventCreatedAt ? eventCreatedAt.getTime() : null;

      // Load usage rows for this event (new table).
      const usageSnap = await getDocs(
        query(collection(db, "ingredient_usage_history"), where("productionEventId", "==", eventId))
      );

      const usageRows = usageSnap.docs.map(d => ({ id: d.id, ref: d.ref, ...d.data() }));

      // If this is a legacy event (no usage rows), fall back to the embedded ingredients array.
      const ingredientDeltas = new Map(); // ingredientId -> qty to add back
      const lotDeltas = new Map(); // lotId -> qty to add back

      for (const row of usageRows) {
        const ingredientId = row?.ingredientId;
        const qty = Number(row?.quantityUsed);
        if (ingredientId && Number.isFinite(qty) && qty !== 0) {
          ingredientDeltas.set(ingredientId, (ingredientDeltas.get(ingredientId) || 0) + qty);
        }

        const lotId = row?.lotId;
        if (lotId && Number.isFinite(qty) && qty !== 0) {
          lotDeltas.set(lotId, (lotDeltas.get(lotId) || 0) + qty);
        }
      }

      if (usageRows.length === 0) {
        const ingredients = Array.isArray(event?.ingredients) ? event.ingredients : [];
        for (const ing of ingredients) {
          const ingredientId = ing?.ingredientId;
          const qty = Number(ing?.quantityStockUnit ?? ing?.quantity);
          if (ingredientId && Number.isFinite(qty) && qty !== 0) {
            ingredientDeltas.set(ingredientId, (ingredientDeltas.get(ingredientId) || 0) + qty);
          }
        }
      }

      // Best-effort detection of event-created legacy lots (created to bridge stock/lot mismatch).
      // These should be removed when deleting the production event.
      const legacyLotIdsToDelete = new Set();
      const ingredientIdsToScan = new Set();
      for (const row of usageRows) {
        if (row?.ingredientId) ingredientIdsToScan.add(row.ingredientId);
      }
      if (ingredientIdsToScan.size === 0) {
        const ingredients = Array.isArray(event?.ingredients) ? event.ingredients : [];
        for (const ing of ingredients) {
          if (ing?.ingredientId) ingredientIdsToScan.add(ing.ingredientId);
        }
      }

      if (eventCreatedAtMs !== null && ingredientIdsToScan.size > 0) {
        const scans = await Promise.all(
          Array.from(ingredientIdsToScan).map(async (ingredientId) => {
            const snap = await getDocs(
              query(collection(db, "ingredient_inventory_lots"), where("ingredientId", "==", ingredientId))
            );
            return snap.docs.map(d => ({ id: d.id, ...d.data() }));
          })
        );

        for (const lots of scans) {
          for (const lot of lots) {
            if (!lot?.isLegacy) continue;
            if (!isEpochDate(lot?.purchaseDate)) continue;
            if (lot?.purchaseHistoryId) continue;
            const createdAt = toJsDate(lot?.createdAt);
            const createdAtMs = createdAt ? createdAt.getTime() : null;
            if (createdAtMs === null || createdAtMs !== eventCreatedAtMs) continue;
            legacyLotIdsToDelete.add(lot.id);
          }
        }
      }

      const batch = writeBatch(db);

      // Restore ingredient stocks.
      for (const [ingredientId, qty] of ingredientDeltas.entries()) {
        const ingredientRef = doc(db, "ingredients", ingredientId);
        batch.update(ingredientRef, {
          currentStock: increment(qty),
          lastUpdated: now
        });
      }

      // Restore lot remaining quantities, unless the lot was event-created legacy (then delete it).
      for (const [lotId, qty] of lotDeltas.entries()) {
        const lotRef = doc(db, "ingredient_inventory_lots", lotId);
        if (legacyLotIdsToDelete.has(lotId)) {
          batch.delete(lotRef);
        } else {
          batch.update(lotRef, {
            remainingQuantity: increment(qty),
            updatedAt: now
          });
        }
      }

      // Delete usage rows (new table).
      for (const row of usageRows) {
        batch.delete(row.ref);
      }

      // Finally delete the production event.
      batch.delete(eventRef);

      await batch.commit();
      setProductionEvents(productionEvents.filter(event => event.id !== eventId));
    } catch (err) {
      console.error("Error deleting production event:", err);
      alert("Failed to delete production record.");
    }
  };

  // Aggregate statistics
  const totalProduced = productionEvents.reduce((acc, evt) => acc + (evt.quantityProduced || 0), 0);
  
  const byProduct = {};
  const byRecipe = {};
  productionEvents.forEach(evt => {
    if (!byProduct[evt.productName]) byProduct[evt.productName] = 0;
    byProduct[evt.productName] += evt.quantityProduced || 0;
    
    const recipeKey = `${evt.productName} - ${evt.recipeName}`;
    if (!byRecipe[recipeKey]) byRecipe[recipeKey] = 0;
    byRecipe[recipeKey] += evt.quantityProduced || 0;
  });

  // Get unique products for filter
  const uniqueProducts = [...new Set(productionEvents.map(evt => evt.productName))].filter(Boolean);

  // Get products with final recipes
  const productsWithFinalRecipes = products.filter(product => 
    product.recipes && product.recipes.some(recipe => recipe.isFinal)
  ).map(product => ({
    ...product,
    finalRecipe: product.recipes.find(recipe => recipe.isFinal)
  }));

  // Get unique products with final recipes that have production events
  const finalRecipeProducts = productsWithFinalRecipes
    .filter(product => uniqueProducts.includes(product.name))
    .map(product => product.name);

  // Set default selected graph product to first available final recipe product
  useEffect(() => {
    if (finalRecipeProducts.length > 0 && !selectedGraphProduct) {
      setSelectedGraphProduct(finalRecipeProducts[0]);
    }
  }, [finalRecipeProducts, selectedGraphProduct]);

  // Prepare time series data for the selected product
  const getTimeSeriesData = () => {
    if (!selectedGraphProduct) return [];
    
    const productEvents = productionEvents.filter(evt => evt.productName === selectedGraphProduct);
    
    // Group by date and sum quantities
    const dailyProduction = {};
    productEvents.forEach(evt => {
      if (!evt.date) return;
      
      const date = new Date(evt.date.seconds * 1000);
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      if (!dailyProduction[dateKey]) {
        dailyProduction[dateKey] = 0;
      }
      dailyProduction[dateKey] += evt.quantityProduced || 0;
    });
    
    // Convert to array and sort by date
    return Object.entries(dailyProduction)
      .map(([date, quantity]) => ({ date, quantity }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const timeSeriesData = getTimeSeriesData();
  const maxQuantity = Math.max(...timeSeriesData.map(d => d.quantity), 0);

  // Filter and sort events
  let filteredEvents = productionEvents;
  if (filterProduct) {
    filteredEvents = productionEvents.filter(evt => evt.productName === filterProduct);
  }

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case "date":
        aVal = a.date ? new Date(a.date.seconds * 1000) : new Date(0);
        bVal = b.date ? new Date(b.date.seconds * 1000) : new Date(0);
        break;
      case "product":
        aVal = a.productName || "";
        bVal = b.productName || "";
        break;
      case "quantity":
        aVal = a.quantityProduced || 0;
        bVal = b.quantityProduced || 0;
        break;
      default:
        return 0;
    }
    
    if (sortOrder === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Get recent production (last 7 days)
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  const recentProduction = productionEvents.filter(evt => {
    if (!evt.date) return false;
    const eventDate = new Date(evt.date.seconds * 1000);
    return eventDate >= sevenDaysAgo;
  });

  const recentTotal = recentProduction.reduce((acc, evt) => acc + (evt.quantityProduced || 0), 0);

  return (
    <div className={styles.dashboardContainer}>
      <h2 className={styles.title}>Production Analytics Dashboard</h2>

      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => navigate("/production-predictor")}
        >
          Open Predictor
        </button>
      </div>
      
      {loading ? (
        <p>Loading production data...</p>
      ) : (
        <>
          {/* Summary Statistics */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <h3>Total Production</h3>
              <p className={styles.statNumber}>{totalProduced}</p>
              <span className={styles.statLabel}>units produced</span>
            </div>
            <div className={styles.statCard}>
              <h3>Recent Production</h3>
              <p className={styles.statNumber}>{recentTotal}</p>
              <span className={styles.statLabel}>last 7 days</span>
            </div>
            <div className={styles.statCard}>
              <h3>Total Events</h3>
              <p className={styles.statNumber}>{productionEvents.length}</p>
              <span className={styles.statLabel}>production records</span>
            </div>
            <div className={styles.statCard}>
              <h3>Products</h3>
              <p className={styles.statNumber}>{uniqueProducts.length}</p>
              <span className={styles.statLabel}>different products</span>
            </div>
          </div>

          {/* Production by Product */}
          <div className={styles.chartSection}>
            <h3>Production by Product</h3>
            <div className={styles.chartGrid}>
              {Object.entries(byProduct).map(([product, qty]) => (
                <div key={product} className={styles.chartItem}>
                  <span className={styles.chartLabel}>{product}</span>
                  <div className={styles.chartBar}>
                    <div 
                      className={styles.chartFill}
                      style={{ width: `${(qty / Math.max(...Object.values(byProduct))) * 100}%` }}
                    ></div>
                  </div>
                  <span className={styles.chartValue}>{qty}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Production by Recipe */}
          <div className={styles.chartSection}>
            <h3>Production by Recipe</h3>
            <div className={styles.chartGrid}>
              {Object.entries(byRecipe).map(([recipe, qty]) => (
                <div key={recipe} className={styles.chartItem}>
                  <span className={styles.chartLabel}>{recipe}</span>
                  <div className={styles.chartBar}>
                    <div 
                      className={styles.chartFill}
                      style={{ width: `${(qty / Math.max(...Object.values(byRecipe))) * 100}%` }}
                    ></div>
                  </div>
                  <span className={styles.chartValue}>{qty}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Time Series Graph for Final Recipe Products */}
          {finalRecipeProducts.length > 0 && (
            <div className={styles.chartSection}>
              <div className={styles.graphHeader}>
                <h3>Production Timeline - Final Recipe Products</h3>
                <div className={styles.graphControls}>
                  <label>Product:</label>
                  <select 
                    value={selectedGraphProduct} 
                    onChange={(e) => setSelectedGraphProduct(e.target.value)}
                    className={styles.graphSelect}
                  >
                    {finalRecipeProducts.map(product => (
                      <option key={product} value={product}>{product}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {timeSeriesData.length === 0 ? (
                <p className={styles.emptyGraph}>No production data available for {selectedGraphProduct}</p>
              ) : (
                <div className={styles.timeSeriesGraph}>
                  <div className={styles.graphContainer}>
                    <div className={styles.yAxis}>
                      <div className={styles.yAxisLabel}>Quantity</div>
                      <div className={styles.yAxisTicks}>
                        <span>{format2(maxQuantity)}</span>
                        <span>{format2(maxQuantity * 0.75)}</span>
                        <span>{format2(maxQuantity * 0.5)}</span>
                        <span>{format2(maxQuantity * 0.25)}</span>
                        <span>{format2(0)}</span>
                      </div>
                    </div>
                    <div className={styles.graphArea}>
                      <div className={styles.graphGrid}>
                        {[1, 2, 3, 4].map(i => (
                          <div key={i} className={styles.gridLine}></div>
                        ))}
                      </div>
                      <div className={styles.graphBars}>
                        {timeSeriesData.map((dataPoint, index) => (
                          <div key={index} className={styles.graphBarContainer}>
                            <div 
                              className={styles.graphBar}
                              style={{
                                height: `${(dataPoint.quantity / maxQuantity) * 100}%`,
                                backgroundColor: `hsl(${210 + (index * 10) % 60}, 70%, 50%)`
                              }}
                              title={`${dataPoint.date}: ${format2(dataPoint.quantity)} units`}
                            ></div>
                            <div className={styles.xAxisLabel}>
                              {new Date(dataPoint.date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className={styles.graphLegend}>
                    <span className={styles.legendItem}>
                      <span className={styles.legendColor}></span>
                      Daily Production for {selectedGraphProduct}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filters and Controls */}
          <div className={styles.controls}>
            <div className={styles.filterGroup}>
              <label>Filter by Product:</label>
              <select 
                value={filterProduct} 
                onChange={(e) => setFilterProduct(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="">All Products</option>
                {uniqueProducts.map(product => (
                  <option key={product} value={product}>{product}</option>
                ))}
              </select>
            </div>
            
            <div className={styles.filterGroup}>
              <label>Sort by:</label>
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="date">Date</option>
                <option value="product">Product</option>
                <option value="quantity">Quantity</option>
              </select>
            </div>
            
            <div className={styles.filterGroup}>
              <label>Order:</label>
              <select 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>

          {/* Production History Table */}
          <div className={styles.tableSection}>
            <h3>Production History ({filteredEvents.length} records)</h3>
            {sortedEvents.length === 0 ? (
              <p className={styles.emptyState}>No production records found. Start producing to see analytics!</p>
            ) : (
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Product</th>
                      <th>Recipe</th>
                      <th>Quantity</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEvents.map(evt => (
                      <tr key={evt.id}>
                        <td>{evt.date ? new Date(evt.date.seconds * 1000).toLocaleString() : "Unknown"}</td>
                        <td>{evt.productName || "Unknown"}</td>
                        <td>{evt.recipeName || "Unknown"}</td>
                        <td>{evt.quantityProduced || 0}</td>
                        <td>
                          <button
                            onClick={() => handleDeleteEvent(evt.id)}
                            className={styles.deleteButton}
                            title="Delete production record"
                          >
                            🗑️ Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
